import type { Manifest } from "../manifest/schema.js";
import type { TtsAdapter } from "../ai/voice.js";
import { h } from "../ui/dom.js";

type Mystery = NonNullable<Manifest["mall"]["mystery"]>;

export interface MysteryDeps {
  tts: TtsAdapter;
  tellerName: string;
  onSpeaking: (on: boolean) => void;
  announce: (msg: string) => void;
}

/** "Hear the mystery": the teller voices each paragraph in turn. Silent until the guest presses Play. */
export function mysteryContent(m: Mystery, d: MysteryDeps): { root: HTMLElement; stop: () => void } {
  let playing = false;
  let stopped = false;
  const paras = m.lines.map((l, i) => h("p", { class: "mystery-line", "data-line": i }, l));
  const btn = h("button", { type: "button", class: "btn btn-primary", "data-testid": "mystery-play" }, "Play");
  const stop = (): void => {
    stopped = true;
    playing = false;
    d.tts.cancel();
    d.onSpeaking(false);
    btn.textContent = "Play";
    for (const p of paras) p.classList.remove("is-speaking");
  };
  btn.addEventListener("click", () => {
    if (playing) return stop();
    playing = true;
    stopped = false;
    btn.textContent = "Stop";
    d.announce(`${d.tellerName} begins the story.`);
    void (async () => {
      for (const [i, line] of m.lines.entries()) {
        if (stopped) return;
        paras.forEach((p, j) => p.classList.toggle("is-speaking", i === j));
        d.onSpeaking(true);
        await d.tts.speak(line);
      }
      if (!stopped) stop();
    })();
  });
  const root = h(
    "div",
    { class: "mystery", "data-testid": "mystery" },
    h("h2", { "data-autofocus": true, tabindex: -1 }, m.title),
    m.placeholder ? h("p", { class: "pad-badge mystery-placeholder", "data-testid": "mystery-placeholder" }, "Placeholder script - awaiting the partner's mystery") : null,
    h("p", { class: "muted small" }, `Told by ${d.tellerName}.`),
    btn,
    h("div", { class: "mystery-text" }, ...paras)
  );
  return { root, stop };
}
